/**
 * The 24-word recovery phrase.
 *
 * BIP-39's encoding, used purely as a human-transcribable representation of 256
 * bits. Everything after the encoding is ours: there is no wallet here, no
 * passphrase-stretched seed, no derivation path. The raw entropy goes straight
 * into HKDF in keys.ts to produce the recovery key that unwraps a second copy
 * of the master key.
 *
 * Why BIP-39 rather than inventing a format: the wordlist is chosen so no two
 * words share their first four letters, it carries a checksum that catches a
 * mistyped or swapped word before the user is told "wrong phrase", and people
 * have existing habits for writing one down. Why *not* BIP-39's seed function:
 * PBKDF2-HMAC-SHA512 with 2048 iterations over 256 bits of entropy adds no
 * security — the input is already uniformly random and far past brute force —
 * and it would only invite someone to plug a Mauvely phrase into a wallet.
 *
 * 24 words = 256 bits of entropy + an 8-bit checksum = 264 bits = 24 × 11.
 */

import { WORDLIST, WORD_INDEX } from './wordlist';
import { randomBytes, sha256, type Bytes } from './primitives';

export const PHRASE_WORDS = 24;
export const ENTROPY_BYTES = 32;

/** Bits of the entropy's SHA-256 appended as a checksum: one per 32 bits. */
const CHECKSUM_BITS = (ENTROPY_BYTES * 8) / 32; // 8

export class PhraseError extends Error {
  constructor(
    readonly code: 'length' | 'unknown_word' | 'checksum',
    message: string,
    /** Index of the offending word, when the failure is attributable to one. */
    readonly wordIndex?: number
  ) {
    super(message);
  }
}

/** A fresh phrase and the entropy behind it. */
export async function generatePhrase(): Promise<{ phrase: string[]; entropy: Bytes }> {
  const entropy = randomBytes(ENTROPY_BYTES);
  return { phrase: await entropyToPhrase(entropy), entropy };
}

export async function entropyToPhrase(entropy: Bytes): Promise<string[]> {
  if (entropy.length !== ENTROPY_BYTES) {
    throw new PhraseError('length', `Entropy must be ${ENTROPY_BYTES} bytes.`);
  }

  const digest = await sha256(entropy);
  // The checksum is the leading CHECKSUM_BITS of the digest, which for 8 bits
  // is simply its first byte.
  const bits = [...entropy, digest[0]!]
    .map((b) => b.toString(2).padStart(8, '0'))
    .join('');

  const words: string[] = [];
  for (let i = 0; i < PHRASE_WORDS; i++) {
    words.push(WORDLIST[Number.parseInt(bits.slice(i * 11, i * 11 + 11), 2)]!);
  }
  return words;
}

/**
 * Recover the entropy behind a phrase, or throw a `PhraseError` naming what is
 * wrong and where.
 *
 * Deliberately forgiving about presentation — case, extra whitespace and line
 * breaks are all normalised — because this is typed by someone reading their
 * own handwriting, often from a phone. It is not forgiving about content: a
 * word outside the list or a failed checksum is reported, never guessed at.
 */
export async function phraseToEntropy(input: string[] | string): Promise<Bytes> {
  const words = normalisePhrase(input);

  if (words.length !== PHRASE_WORDS) {
    throw new PhraseError(
      'length',
      `A recovery phrase is ${PHRASE_WORDS} words. That one has ${words.length}.`
    );
  }

  let bits = '';
  for (const [i, word] of words.entries()) {
    const index = WORD_INDEX.get(word);
    if (index === undefined) {
      throw new PhraseError('unknown_word', `"${word}" is not a recovery word.`, i);
    }
    bits += index.toString(2).padStart(11, '0');
  }

  const entropy = new Uint8Array(ENTROPY_BYTES) as Bytes;
  for (let i = 0; i < ENTROPY_BYTES; i++) {
    entropy[i] = Number.parseInt(bits.slice(i * 8, i * 8 + 8), 2);
  }

  const expected = bits.slice(ENTROPY_BYTES * 8);
  const digest = await sha256(entropy);
  const actual = digest[0]!.toString(2).padStart(8, '0').slice(0, CHECKSUM_BITS);

  if (expected !== actual) {
    // Almost always one mistyped or transposed word rather than a wholly wrong
    // phrase, and saying so is the difference between a user trying again and
    // a user concluding their data is gone.
    throw new PhraseError(
      'checksum',
      'That phrase does not check out. One of the words is probably wrong or out of order.'
    );
  }

  return entropy;
}

/** Lowercase, collapse whitespace, drop the numbering people paste in. */
export function normalisePhrase(input: string[] | string): string[] {
  const raw = Array.isArray(input) ? input.join(' ') : input;
  return raw
    .toLowerCase()
    .replace(/[^a-z\s]/g, ' ')
    .split(/\s+/)
    .filter(Boolean);
}

/**
 * Suggestions for a partially typed word.
 *
 * Four characters uniquely identify every word in the list, so this narrows to
 * exactly one result by the fourth keystroke — which is what makes typing 24
 * words tolerable.
 */
export function suggestWords(prefix: string, limit = 5): string[] {
  const p = prefix.trim().toLowerCase();
  if (!p) return [];
  const out: string[] = [];
  // The list is sorted, so matches are contiguous and the scan can stop early.
  for (const word of WORDLIST) {
    if (word.startsWith(p)) {
      out.push(word);
      if (out.length >= limit) break;
    } else if (out.length) {
      break;
    }
  }
  return out;
}

export const isRecoveryWord = (word: string): boolean => WORD_INDEX.has(word.trim().toLowerCase());

/**
 * Positions to quiz the user on when confirming they wrote the phrase down.
 *
 * Three distinct positions, chosen at random rather than fixed, so the check
 * cannot be passed by remembering which boxes to fill from last time.
 */
export function confirmationPositions(count = 3): number[] {
  const picked = new Set<number>();
  while (picked.size < count) {
    picked.add(crypto.getRandomValues(new Uint32Array(1))[0]! % PHRASE_WORDS);
  }
  return [...picked].sort((a, b) => a - b);
}
